import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './PostForm.module.scss';
import Back from '../Icons/Back';
import Close from '../Icons/Close';
import DescriptionEditor from '../DescriptionEditor/DescriptionEditor';
import InlineOpenGraph from '../InlineOpenGraph';
import { useOpenGraph } from '../../hooks/useOpenGraph';
import { useSongs } from '../../config/SongContext';
import AlertDialog from '../AlertDialog';
import { sanitizers } from '../../utils/validation';

import useIsMobile from './hooks/useIsMobile';
import useAlertDialog from './hooks/useAlertDialog';
import useGenreChips, { MAX_GENRE_LENGTH } from './hooks/useGenreChips';
import usePostFormValidation from './hooks/usePostFormValidation';
import useOpenGraphFilter from './hooks/useOpenGraphFilter';
import useYouTubeUrlInput from './hooks/useYouTubeUrlInput';
import useSubmitSong from './hooks/useSubmitSong';

const DESCRIPTION_PLACEHOLDER = 'Tell us about this song...';
const MAX_CHARS = 3000;

const PostForm = ({
  onSuccess,
  onPostingChange,
  isEditing = false,
  postToEdit = null,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [songName, setSongName] = useState('');
  const [songArtist, setSongArtist] = useState('');
  const [remainingChars, setRemainingChars] = useState(MAX_CHARS);
  const [descriptionHTML, setDescriptionHTML] = useState('');

  // Mount flag is read synchronously by several async handlers — keep it
  // in a ref (not state) so they see the current value without having to
  // be re-created on every re-render.
  const mountedRef = useRef(true);
  const isMountedFn = useCallback(() => mountedRef.current, []);

  // TipTap editor ref — exposes getHTML(), getMentionedHandles(),
  // setContent(), clear(), getCharacterCount()
  const editorRef = useRef(null);
  const urlInputRef = useRef(null);
  const genreSelectorRef = useRef(null);

  const isMobile = useIsMobile();
  const { setSongs, songs } = useSongs();

  const { alertDialog, showAlert, hideAlert } = useAlertDialog();

  const {
    selectedGenres,
    setSelectedGenres,
    genreQuery,
    handleRemoveGenre,
    handleGenreQueryChange,
    handleGenreKeyDown,
    reset: resetGenres,
    maxGenreCount,
  } = useGenreChips({
    onOverflow: (max) =>
      showAlert('Genre Too Long', `Genre tags cannot exceed ${max} characters`),
  });

  const handleSongDetailsFromVideo = useCallback(
    ({ songName: nextName, songArtist: nextArtist }) => {
      setSongName(nextName);
      setSongArtist(nextArtist);
    },
    []
  );

  // URL hook clears URL-field validation errors when the user types; that
  // clear function lives in the validation hook which is instantiated
  // after this one, so we forward the call through a ref to break the
  // circular dependency.
  const clearUrlErrorRef = useRef(() => {});
  const onClearUrlError = useCallback(() => {
    clearUrlErrorRef.current();
  }, []);

  const {
    songURL,
    setSongURL,
    isUrlValid,
    setIsUrlValid,
    showFullForm,
    setShowFullForm,
    urlError,
    setUrlError,
    fetchedTitle,
    setFetchedTitle,
    fetchedThumbnail,
    setFetchedThumbnail,
    clearUrlInput,
    handleUrlChange,
    handleUrlPaste,
    handleUrlKeyDown,
  } = useYouTubeUrlInput({
    isMounted: isMountedFn,
    isEditing,
    postToEdit,
    onSongDetails: handleSongDetailsFromVideo,
    onClearUrlError,
  });

  const validation = usePostFormValidation({
    getSanitizedValues: () => ({
      songName: sanitizers.text(songName),
      songArtist: sanitizers.text(songArtist),
      URL: songURL,
      description: sanitizers.html(descriptionHTML),
      genres: selectedGenres
        .map((genre) => sanitizers.text(genre))
        .filter(Boolean),
    }),
  });

  clearUrlErrorRef.current = validation.clearUrlError;

  const {
    openGraphData,
    isLoading: openGraphLoading,
    error: openGraphError,
  } = useOpenGraph(descriptionHTML);

  const { filteredOpenGraphData, handleRemoveOpenGraphCard } =
    useOpenGraphFilter(openGraphData);

  const resetForm = useCallback(() => {
    setSongName('');
    setSongArtist('');
    setSongURL('');
    editorRef.current?.clear();
    setDescriptionHTML('');
    setRemainingChars(MAX_CHARS);
    resetGenres();
  }, [setSongURL, resetGenres]);

  const { handleSubmit } = useSubmitSong({
    editorRef,
    songName,
    songArtist,
    songURL,
    selectedGenres,
    fetchedThumbnail,
    isEditing,
    postToEdit,
    songs,
    setSongs,
    validateAll: validation.validateAll,
    sanitizers,
    showAlert,
    onSuccess,
    onPostingChange,
    onResetForm: resetForm,
    isLoading,
    setIsLoading,
  });

  useEffect(() => {
    mountedRef.current = true;
    if (isEditing && postToEdit) {
      setSongName(postToEdit.songName);
      setSongArtist(postToEdit.songArtist);
      setSongURL(postToEdit.URL);
      setFetchedTitle(postToEdit.songName);

      // Preserve the existing-thumbnail-available invariant the form
      // relies on for edit mode (default.url is the gate), by filling
      // every quality slot with the same stored URL.
      const mockThumbnail = {
        default: { url: postToEdit.thumbnailURL },
        medium: { url: postToEdit.thumbnailURL },
        high: { url: postToEdit.thumbnailURL },
        standard: { url: postToEdit.thumbnailURL },
        maxres: { url: postToEdit.thumbnailURL },
      };
      setFetchedThumbnail(mockThumbnail);

      setSelectedGenres(postToEdit.genres || []);
      // Restore description into TipTap — defer until editor is mounted.
      setTimeout(() => {
        editorRef.current?.setContent(postToEdit.description || '');
      }, 0);
      setShowFullForm(true);
      setIsUrlValid(true);
    }
    return () => {
      mountedRef.current = false;
    };
  }, [
    isEditing,
    postToEdit,
    setSongURL,
    setFetchedTitle,
    setFetchedThumbnail,
    setSelectedGenres,
    setShowFullForm,
    setIsUrlValid,
  ]);

  useEffect(() => {
    if (!isMobile && urlInputRef.current) {
      urlInputRef.current.focus();
    }
  }, [isMobile]);

  const dismissUrlError = useCallback(() => setUrlError(''), [setUrlError]);

  const { validationErrors, touchedFields, handleFieldBlur } = validation;

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit} className={styles.postform}>
        {!showFullForm && (
          <div className={styles.ctaGroup}>
            <label className={styles.inputGroup}>
              <span className={styles.inputLabelCta}>Youtube URL</span>
              <div className={styles.urlInputWrap}>
                <input
                  ref={urlInputRef}
                  className={styles.urlInput}
                  type="url"
                  inputMode="url"
                  value={songURL}
                  onChange={handleUrlChange}
                  onPaste={handleUrlPaste}
                  onKeyDown={handleUrlKeyDown}
                  onBlur={() => handleFieldBlur('URL')}
                  placeholder="Enter a YouTube URL"
                  enterKeyHint="go"
                />
              </div>
              {urlError && (
                <div className={styles.errorContainer}>
                  <span className={styles.errorMessage}>{urlError}</span>
                  <button
                    type="button"
                    className={styles.dismissButton}
                    onClick={dismissUrlError}
                  >
                    Got it
                  </button>
                </div>
              )}
              {touchedFields.URL && validationErrors.URL && (
                <div className={styles.errorContainer}>
                  <span className={styles.errorMessage}>
                    {validationErrors.URL.join(', ')}
                  </span>
                </div>
              )}
              {!isUrlValid && !urlError && !validationErrors.URL && (
                <span className={styles.inputLabel}>
                  ☝️ Please enter a valid YouTube or YouTube Music url
                </span>
              )}
            </label>
          </div>
        )}
        {showFullForm && (
          <div className={styles.fetchedContainer}>
            <div className={styles.fetchedContainerBody}>
              <div className={styles.fetchedContainerLeft}>
                <div className={styles.fetchedContainerHeader}>
                  <button
                    type="button"
                    onClick={clearUrlInput}
                    className={styles.clearButton}
                  >
                    <Back fill="#404040" />
                    <span className={styles.fetchedContainerTitle}>
                      Song info
                    </span>
                  </button>
                </div>
                <div className={styles.songCluster}>
                  {fetchedThumbnail &&
                    (fetchedThumbnail.default?.url ||
                      (isEditing && postToEdit?.thumbnailURL)) && (
                      <>
                        <img
                          className={styles.thumbnail}
                          src={
                            fetchedThumbnail.default?.url ||
                            postToEdit?.thumbnailURL
                          }
                          alt=""
                        />
                        <div className={styles.fetchedInfo}>
                          <span className={styles.fetchedName}>
                            {fetchedTitle}
                          </span>
                          <a
                            href={songURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.fetchedURL}
                          >
                            {songURL}
                          </a>
                        </div>
                      </>
                    )}
                </div>
                <div className={styles.songInfoCluster}>
                  <label className={styles.inputLabel}>
                    Song Name
                    <input
                      className={styles.songInput}
                      type="text"
                      value={songName}
                      onChange={(e) => setSongName(e.target.value)}
                      onBlur={() => handleFieldBlur('songName')}
                    />
                    {touchedFields.songName && validationErrors.songName && (
                      <div className={styles.errorContainer}>
                        <span className={styles.errorMessage}>
                          {validationErrors.songName.join(', ')}
                        </span>
                      </div>
                    )}
                  </label>
                  <label className={styles.inputLabel}>
                    Artist
                    <input
                      className={styles.songInput}
                      type="text"
                      value={songArtist}
                      onChange={(e) => setSongArtist(e.target.value)}
                      onBlur={() => handleFieldBlur('songArtist')}
                    />
                    {touchedFields.songArtist &&
                      validationErrors.songArtist && (
                        <div className={styles.errorContainer}>
                          <span className={styles.errorMessage}>
                            {validationErrors.songArtist.join(', ')}
                          </span>
                        </div>
                      )}
                  </label>
                  <div className={styles.genreSelector} ref={genreSelectorRef}>
                    <label className={styles.inputLabel}>
                      Add up to {maxGenreCount} tags:
                    </label>
                    <input
                      type="text"
                      value={genreQuery}
                      onChange={handleGenreQueryChange}
                      onKeyDown={handleGenreKeyDown}
                      placeholder={
                        selectedGenres.length >= maxGenreCount
                          ? 'Maximum tags selected'
                          : `Type a tag and press enter`
                      }
                      className={`${styles.genreSearchInput} ${
                        selectedGenres.length >= maxGenreCount
                          ? styles.disabled
                          : ''
                      }`}
                      disabled={selectedGenres.length >= maxGenreCount}
                      maxLength={MAX_GENRE_LENGTH}
                    />
                    {selectedGenres.length > 0 && (
                      <div className={styles.genreChips}>
                        {selectedGenres.map((genre) => (
                          <div key={genre} className={styles.chip}>
                            {genre}
                            <button
                              type="button"
                              className={styles.removeButton}
                              onClick={() => handleRemoveGenre(genre)}
                            >
                              <Close fill="#404040" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {touchedFields.genres && validationErrors.genres && (
                      <div className={styles.errorContainer}>
                        <span className={styles.errorMessage}>
                          {validationErrors.genres.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div
                className={styles.fetchedContainerRight}
                onClick={(e) => {
                  // Portaled UI (e.g. link panel) is not a DOM descendant
                  // but still bubbles in the React tree — don't steal
                  // focus from those controls.
                  if (e.target.closest?.('[data-link-ui]')) return;
                  editorRef.current?.focus();
                }}
              >
                <div className={styles.fetchedContainerRightInner}>
                  <div className={styles.descriptionCluster}>
                    <p className={styles.descriptionLabel}>
                      What do you have to say about this song?
                    </p>
                    <p className={styles.remainingChars}>{remainingChars}</p>
                  </div>
                  <div className={styles.textBox}>
                    <DescriptionEditor
                      ref={editorRef}
                      placeholder={DESCRIPTION_PLACEHOLDER}
                      maxLength={MAX_CHARS}
                      onChange={(html, charCount) => {
                        setDescriptionHTML(html);
                        setRemainingChars(MAX_CHARS - charCount);
                      }}
                      onBlur={() => handleFieldBlur('description')}
                    />
                    <InlineOpenGraph
                      openGraphData={filteredOpenGraphData}
                      isLoading={openGraphLoading}
                      error={openGraphError}
                      onRemoveCard={handleRemoveOpenGraphCard}
                      className={styles.inlineOpenGraph}
                    />
                  </div>
                  <div className={styles.submitCluster}>
                    <button
                      className={styles.submitCta}
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading
                        ? isEditing
                          ? 'Updating…'
                          : 'Submitting…'
                        : isEditing
                          ? 'Update'
                          : 'Submit'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>

      <AlertDialog
        show={alertDialog.show}
        onClose={hideAlert}
        title={alertDialog.title}
        message={alertDialog.message}
        confirmLabel={alertDialog.confirmLabel}
        confirmColor={alertDialog.confirmColor}
        showCancel={alertDialog.showCancel}
        onConfirm={alertDialog.onConfirm}
      />
    </div>
  );
};

export default PostForm;
